import React, { FunctionComponent, ReactNode } from "react";
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { useI18n } from "../../hooks/useI18n";

interface WeekDayMultiSelectProps extends Omit<SelectProps, "loading"> {
  onSelected?: (value: ReadonlyArray<string>) => void;
  onChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => void;
  label?: string;
  variant?: "filled" | "outlined" | "standard";
  value?: ReadonlyArray<string>;
  disabled?: boolean;
}

export const WeekDayMultiSelect: FunctionComponent<WeekDayMultiSelectProps> = ({
  label = "Week days",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  disabled = false,
  ...props
}) => {
  const { t } = useI18n();

  const weekDays = [
    { value: "Monday", label: t("common.time.monday") },
    { value: "Tuesday", label: t("common.time.tuesday") },
    { value: "Wednesday", label: t("common.time.wednesday") },
    { value: "Thursday", label: t("common.time.thursday") },
    { value: "Friday", label: t("common.time.friday") },
    { value: "Saturday", label: t("common.time.saturday") },
    { value: "Sunday", label: t("common.time.sunday") }
  ];

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let selectedWeekDays = event.target.value as ReadonlyArray<string>;
      onSelected(selectedWeekDays);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (weekDays: ReadonlyArray<string>): ReactNode => {
    return weekDays.join(", ");
  };

  const rnd = Math.floor(Math.random() * 100 + 1);
  const id = `weekday-multi-select-${rnd}`;

  return (
    <FormControl
      variant={variant}
      className={props.className}
      error={props.error}
    >
      <InputLabel
        id={`${id}-label`}
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId={`${id}-label`}
        id={id}
        autoWidth={true}
        input={<Input />}
        disabled={disabled}
        renderValue={value => renderNames(value as string[])}
      >
        {weekDays.map(item => (
          <MenuItem key={`${rnd}-${item.value}`} value={item.value}>
            <Checkbox checked={value.indexOf(item.value) > -1} />
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
